<template>
  <div class="modal d-block" tabindex="-1" @click.self="$emit('close')">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ t("ecolabels.manage_devices.title") }}
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')" />
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ statement.file_name }}</strong>
                <span v-if="statement.total_mwh" class="badge bg-info ms-2">
                  {{ statement.total_mwh }} MWh
                </span>
              </div>
              <span
                class="badge rounded-pill"
                :class="statementStatusBadgeClass(statement.status)"
              >
                {{ statement.status }}
              </span>
            </div>
          </div>

          <section class="mb-3">
            <h6 class="fw-semibold mb-2">
              {{ t("ecolabels.manage_devices.documents_section") }}
            </h6>
            <div class="card border-translucent">
              <div class="card-body">
                <div
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <div>
                    <div class="fw-semibold">
                      {{ t("ecolabels.manage_devices.statement_document") }}
                    </div>
                    <small class="text-muted">{{ statementFileName }}</small>
                  </div>
                  <div class="btn-group btn-group-sm">
                    <button
                      v-if="statementUploadId"
                      class="btn btn-outline-secondary"
                      @click="downloadPrimaryStatementDocument"
                    >
                      <font-awesome-icon icon="download" />
                    </button>
                    <button
                      v-if="statementUploadId"
                      class="btn btn-outline-danger"
                      :disabled="deletingStatementDoc"
                      @click="deleteStatementDocument"
                    >
                      <span
                        v-if="deletingStatementDoc"
                        class="spinner-border spinner-border-sm"
                      />
                      <font-awesome-icon v-else icon="trash" />
                    </button>
                    <button
                      v-else
                      class="btn btn-outline-primary"
                      :disabled="uploadingStatementDoc"
                      @click="triggerStatementFileInput"
                    >
                      <span
                        v-if="uploadingStatementDoc"
                        class="spinner-border spinner-border-sm"
                      />
                      <template v-else>
                        <font-awesome-icon icon="plus" class="me-1" />
                        {{ t("ecolabels.manage_devices.add_document") }}
                      </template>
                    </button>
                  </div>
                </div>
                <div
                  v-if="statementDocuments.length > 1"
                  class="small text-muted mb-2"
                >
                  {{ statementDocuments.length }}
                  {{ t("ecolabels.manage_devices.statement_document") }}
                </div>
                <div
                  v-if="statementDocuments.length > 0"
                  class="d-flex flex-column gap-1 mb-2"
                >
                  <div
                    v-for="doc in statementDocuments"
                    :key="doc.upload_id"
                    class="d-flex align-items-center justify-content-between border rounded px-2 py-1"
                  >
                    <small class="text-truncate pe-2">{{
                      doc.file_name
                    }}</small>
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      @click="
                        downloadStatementDocument(doc.upload_id, doc.file_name)
                      "
                    >
                      <font-awesome-icon icon="download" />
                    </button>
                  </div>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <div class="fw-semibold">
                      {{ t("ecolabels.manage_devices.certificate_document") }}
                    </div>
                    <small class="text-muted">
                      {{
                        certificateUploadId
                          ? `ecolabel-certificate-${props.statement.id}.pdf`
                          : t(
                              "ecolabels.manage_devices.no_certificate_document"
                            )
                      }}
                    </small>
                  </div>
                  <div class="btn-group btn-group-sm">
                    <button
                      v-if="certificateUploadId"
                      class="btn btn-outline-secondary"
                      @click="downloadCertificateDocument"
                    >
                      <font-awesome-icon icon="download" />
                    </button>
                    <button
                      v-if="certificateUploadId"
                      class="btn btn-outline-danger"
                      :disabled="deletingCertificateDoc"
                      @click="deleteCertificateDocument"
                    >
                      <span
                        v-if="deletingCertificateDoc"
                        class="spinner-border spinner-border-sm"
                      />
                      <font-awesome-icon v-else icon="trash" />
                    </button>
                    <button
                      v-else-if="props.isAdmin"
                      class="btn btn-outline-primary"
                      :disabled="uploadingCertificateDoc"
                      @click="triggerCertificateFileInput"
                    >
                      <span
                        v-if="uploadingCertificateDoc"
                        class="spinner-border spinner-border-sm"
                      />
                      <template v-else>
                        <font-awesome-icon icon="plus" class="me-1" />
                        {{ t("ecolabels.manage_devices.add_document") }}
                      </template>
                    </button>
                  </div>
                </div>

                <input
                  ref="statementFileInput"
                  type="file"
                  class="d-none"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                  @change="onStatementFileSelected"
                />
                <input
                  ref="certificateFileInput"
                  type="file"
                  class="d-none"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                  @change="onCertificateFileSelected"
                />
              </div>
            </div>
          </section>

          <section class="mb-3">
            <div class="mb-3">
              <label class="form-label fw-semibold">
                {{ t("ecolabels.upload_wizard.seller_company") }}
              </label>
              <select
                v-model="selectedCompanyId"
                class="form-select"
                :disabled="loadingCompanies || availableCompanies.length === 0"
              >
                <option
                  v-for="company in availableCompanies"
                  :key="company.id"
                  :value="company.id"
                >
                  {{ company.name }}
                </option>
              </select>
            </div>
            <h6 class="fw-semibold mb-2">
              {{ t("ecolabels.manage_devices.statement_details") }}
            </h6>
            <div class="card border-translucent">
              <div class="card-body">
                <CancelGoStatementComponent
                  :statement="statementForm"
                  :statement-index="0"
                  :show-section-titles="true"
                  @update:statement="handleStatementUpdate"
                />
              </div>
            </div>
          </section>

          <!-- Current Device Assignments -->
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>{{
                t("ecolabels.manage_devices.linked_devices")
              }}</strong>
              <button
                class="btn btn-outline-primary btn-sm"
                @click="showPicker = true"
              >
                <font-awesome-icon icon="plus" class="me-1" />
                {{ t("ecolabels.manage_devices.add_device") }}
              </button>
            </div>

            <div
              v-if="deviceLinks.length === 0"
              class="text-center py-3 text-muted border rounded"
            >
              {{ t("ecolabels.manage_devices.no_devices") }}
            </div>

            <div v-else class="table-responsive">
              <table class="table table-sm align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>{{ t("ecolabels.table.name") }}</th>
                    <th>{{ t("ecolabels.table.country") }}</th>
                    <th class="mwh-column">MWh</th>
                    <th class="action-column"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(link, index) in deviceLinks"
                    :key="link.device_id"
                  >
                    <td class="fw-semibold">{{ link.device_name }}</td>
                    <td>{{ link.device_country }}</td>
                    <td>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        :value="link.mwh_attributed"
                        min="0"
                        @input="updateMwh(index, $event)"
                      />
                    </td>
                    <td>
                      <button
                        class="btn btn-link btn-sm text-danger p-0"
                        @click="removeDevice(index)"
                      >
                        <font-awesome-icon icon="times" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Device Picker -->
          <div v-if="showPicker" class="card border-primary mb-3">
            <div
              class="card-header bg-light d-flex justify-content-between align-items-center py-2"
            >
              <strong>{{ t("ecolabels.manage_devices.select_device") }}</strong>
              <button
                class="btn btn-link btn-sm p-0"
                @click="showPicker = false"
              >
                <font-awesome-icon icon="times" />
              </button>
            </div>
            <div class="card-body p-2">
              <input
                v-model="searchQuery"
                type="text"
                class="form-control form-control-sm mb-2"
                :placeholder="t('ecolabels.upload_wizard.search_devices')"
              />
              <div class="device-picker-list">
                <div
                  v-for="device in filteredDevices"
                  :key="device.id"
                  class="d-flex align-items-center justify-content-between py-1 px-2 border-bottom cursor-pointer picker-row"
                  @click="addDevice(device)"
                >
                  <div>
                    <span class="fw-semibold">{{ device.name }}</span>
                    <small class="text-muted ms-2">{{ device.country }}</small>
                  </div>
                  <font-awesome-icon icon="plus" class="text-primary" />
                </div>
                <div
                  v-if="filteredDevices.length === 0"
                  class="text-center py-2 text-muted"
                >
                  {{ t("ecolabels.upload_wizard.no_devices_found") }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="$emit('close')">
            {{ t("common.cancel") }}
          </button>
          <button
            class="btn btn-primary"
            :disabled="saving"
            @click="saveChanges"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" />
            {{ t("common.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" />
</template>

<script setup lang="ts">
import { useFileDownload } from "@/composables/useFileDownload";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import CancelGoStatementComponent from "@/dashboard/inventory_management/company_orders/CancelGoStatementComponent.vue";
import type { BeneficiaryTypeEnum, IndividualCancelStatement } from "@/types";
import { statementStatusBadgeClass } from "@/ecolabels/statementStatus";
import { normalizeRegistryCountryRegion } from "@/utils/registries";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type {
  CancellationStatement,
  CancellationStatementDeviceResponse,
  EcolabelDevice,
  AuthorizedSellerCompany,
  StatementDocument,
} from "../api/ecolabelsTypes";

interface DeviceLink {
  device_id: number;
  device_name: string;
  device_country: string;
  mwh_attributed: number | null;
}

const props = defineProps<{
  statement: CancellationStatement;
  allDevices: EcolabelDevice[];
  isAdmin: boolean;
}>();

const emit = defineEmits<{
  close: [];
  saved: [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { downloadCompanyDocument } = useFileDownload();
const saving = ref(false);
const showPicker = ref(false);
const searchQuery = ref("");
const loadingCompanies = ref(false);
const availableCompanies = ref<AuthorizedSellerCompany[]>([]);
const selectedCompanyId = ref<number>(props.statement.company_id);
const currentCompanyId = ref<number>(props.statement.company_id);
const statementUploadId = ref<number | null>(
  props.statement.statement_upload_id
);
const certificateUploadId = ref<number | null>(
  props.statement.certificate_upload_id
);
const statementFileName = ref<string>(props.statement.file_name);
const statementDocuments = ref<StatementDocument[]>(
  props.statement.statement_documents ?? []
);
const uploadingStatementDoc = ref(false);
const uploadingCertificateDoc = ref(false);
const deletingStatementDoc = ref(false);
const deletingCertificateDoc = ref(false);
const statementFileInput =
  useTemplateRef<HTMLInputElement>("statementFileInput");
const certificateFileInput = useTemplateRef<HTMLInputElement>(
  "certificateFileInput"
);
// Ecolabel-specific notes field (separate from cancellation_purpose)
const ecolabelNotes = ref<string>(props.statement.notes ?? "");

const statementForm = ref<IndividualCancelStatement>({
  temp_id: "",
  beneficiary_name: props.statement.beneficiary_name ?? "",
  beneficiary_type: "CONSUMER" as BeneficiaryTypeEnum,
  registry_code: props.statement.registry_code ?? "",
  registry_country_region:
    normalizeRegistryCountryRegion(props.statement.country) ?? "",
  address: "",
  period_start: props.statement.period_start ?? "",
  period_end: props.statement.period_end ?? "",
  cancellation_purpose: (props.statement.purpose as string) || "",
  mwh_cancelled: props.statement.total_mwh ?? null,
  isCollapsed: false,
});

const deviceLinks = ref<DeviceLink[]>(
  props.statement.devices.map((d: CancellationStatementDeviceResponse) => ({
    device_id: d.device_id,
    device_name: d.device_name,
    device_country: d.device_country,
    mwh_attributed: d.mwh_attributed,
  }))
);

const linkedDeviceIds = computed(
  () => new Set(deviceLinks.value.map((l) => l.device_id))
);

const filteredDevices = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return props.allDevices.filter(
    (d) =>
      !linkedDeviceIds.value.has(d.id) &&
      (d.name.toLowerCase().includes(query) ||
        d.country.toLowerCase().includes(query))
  );
});

const addDevice = (device: EcolabelDevice) => {
  deviceLinks.value.push({
    device_id: device.id,
    device_name: device.name,
    device_country: device.country,
    mwh_attributed: null,
  });
  showPicker.value = false;
  searchQuery.value = "";
};

const removeDevice = (index: number) => {
  deviceLinks.value.splice(index, 1);
};

const updateMwh = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  deviceLinks.value[index].mwh_attributed = parseInt(target.value) || null;
};

const handleStatementUpdate = (updatedStatement: IndividualCancelStatement) => {
  statementForm.value = updatedStatement;
};

const applyStatementResponse = (updated: CancellationStatement) => {
  statementUploadId.value = updated.statement_upload_id;
  certificateUploadId.value = updated.certificate_upload_id;
  statementFileName.value = updated.file_name;
  currentCompanyId.value = updated.company_id;
  statementDocuments.value = updated.statement_documents ?? [];
};

const triggerStatementFileInput = () => statementFileInput.value?.click();
const triggerCertificateFileInput = () => certificateFileInput.value?.click();

const onStatementFileSelected = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  target.value = "";
  if (!file) return;

  uploadingStatementDoc.value = true;
  try {
    const { data } = await ecolabelsApi.replaceStatementDocument(
      props.statement.id,
      "statement",
      file
    );
    applyStatementResponse(data);
    globalSuccessHandler(
      t("ecolabels.manage_devices.statement_document_updated"),
      ""
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    uploadingStatementDoc.value = false;
  }
};

const onCertificateFileSelected = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  target.value = "";
  if (!file) return;

  uploadingCertificateDoc.value = true;
  try {
    const { data } = await ecolabelsApi.replaceStatementDocument(
      props.statement.id,
      "certificate",
      file
    );
    applyStatementResponse(data);
    globalSuccessHandler(
      t("ecolabels.manage_devices.certificate_document_updated"),
      ""
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    uploadingCertificateDoc.value = false;
  }
};

const deleteStatementDocument = async () => {
  deletingStatementDoc.value = true;
  try {
    const { data } = await ecolabelsApi.deleteStatementDocument(
      props.statement.id,
      "statement"
    );
    applyStatementResponse(data);
    globalSuccessHandler(
      t("ecolabels.manage_devices.statement_document_deleted"),
      ""
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    deletingStatementDoc.value = false;
  }
};

const deleteCertificateDocument = async () => {
  deletingCertificateDoc.value = true;
  try {
    const { data } = await ecolabelsApi.deleteStatementDocument(
      props.statement.id,
      "certificate"
    );
    applyStatementResponse(data);
    globalSuccessHandler(
      t("ecolabels.manage_devices.certificate_document_deleted"),
      ""
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    deletingCertificateDoc.value = false;
  }
};

const downloadStatementDocument = async (
  uploadId?: number | null,
  fileName?: string
) => {
  const targetUploadId = uploadId ?? statementUploadId.value;
  if (!targetUploadId) return;
  await downloadCompanyDocument(currentCompanyId.value, targetUploadId, {
    filename:
      fileName ??
      statementFileName.value ??
      `statement-${props.statement.id}.txt`,
  });
};

const downloadPrimaryStatementDocument = async () => {
  await downloadStatementDocument();
};

const downloadCertificateDocument = async () => {
  if (!certificateUploadId.value) return;
  await downloadCompanyDocument(
    currentCompanyId.value,
    certificateUploadId.value,
    {
      filename: `ecolabel-certificate-${props.statement.id}.pdf`,
    }
  );
};

const loadAuthorizedSellerCompanies = async () => {
  loadingCompanies.value = true;
  try {
    const { data } = await ecolabelsApi.listAuthorizedSellerCompanies();
    availableCompanies.value = data.companies;
    const hasSelected = data.companies.some(
      (c) => c.id === selectedCompanyId.value
    );
    if (!hasSelected && data.companies.length > 0) {
      selectedCompanyId.value = data.companies[0].id;
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loadingCompanies.value = false;
  }
};

const saveChanges = async () => {
  saving.value = true;
  try {
    await ecolabelsApi.updateStatement(props.statement.id, {
      company_id: selectedCompanyId.value || null,
      beneficiary_name: statementForm.value.beneficiary_name || null,
      registry_code: statementForm.value.registry_code || null,
      country: statementForm.value.registry_country_region || null,
      period_start: statementForm.value.period_start || null,
      period_end: statementForm.value.period_end || null,
      purpose: statementForm.value.cancellation_purpose || null,
      total_mwh:
        typeof statementForm.value.mwh_cancelled === "number"
          ? statementForm.value.mwh_cancelled
          : null,
      notes: ecolabelNotes.value || null,
      device_links: deviceLinks.value.map((l) => ({
        device_id: l.device_id,
        mwh_attributed: l.mwh_attributed,
      })),
    });
    globalSuccessHandler(t("ecolabels.manage_devices.saved"), "");
    emit("saved");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  void loadAuthorizedSellerCompanies();
});
</script>

<style scoped lang="scss">
.mwh-column {
  width: 120px;
}

.action-column {
  width: 50px;
}

.device-picker-list {
  max-height: 200px;
  overflow-y: auto;
}

.picker-row {
  cursor: pointer;

  &:hover {
    background-color: rgba(var(--phoenix-primary-rgb), 0.05);
  }
}
</style>
